import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

interface NetworkStatus {
  isOnline: boolean;
  isConnected: boolean;
  retryCount: number;
  lastChecked: Date | null;
}

// Helper to create an abort signal with timeout that works on older Android WebViews
// AbortSignal.timeout() is not supported on Android WebView < 127 (Baseline 2024)
const createTimeoutSignal = (ms: number): AbortSignal => {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
};

export function useNetworkStatus() {
  const [status, setStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    isConnected: true, // Assume connected initially to not block UI
    retryCount: 0,
    lastChecked: null
  });

  const checkConnection = async (): Promise<boolean> => {
    const isNative = Capacitor.isNativePlatform();
    
    // On native, trust navigator.onLine immediately - it's more reliable than fetch tests
    // which can fail due to CORS, SSL issues, or WebView quirks
    if (isNative) {
      return navigator.onLine;
    }
    
    // On web, do a quick fetch test to verify actual connectivity.
    // Any HTTP response (even 401/403/404) proves we reached the network —
    // only a thrown error (DNS / offline / timeout) means we're truly offline.
    try {
      const testUrl = window.location.origin + '/favicon.ico';
      await fetch(testUrl, {
        method: 'HEAD',
        cache: 'no-cache',
        signal: createTimeoutSignal(3000)
      });
      return true;
    } catch {
      return navigator.onLine;
    }
  };

  const testConnectivity = async () => {
    const isConnected = await checkConnection();
    setStatus(prev => ({
      ...prev,
      isConnected,
      isOnline: navigator.onLine,
      lastChecked: new Date(),
      retryCount: isConnected ? 0 : prev.retryCount + 1
    }));
    return isConnected;
  };

  const retry = () => {
    return testConnectivity();
  };

  useEffect(() => {
    const handleOnline = () => {
      setStatus(prev => ({ ...prev, isOnline: true, isConnected: true }));
    };

    const handleOffline = () => {
      setStatus(prev => ({ ...prev, isOnline: false, isConnected: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connectivity check
    testConnectivity();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const hasConnection = status.isOnline && status.isConnected;

  return {
    ...status,
    retry,
    hasConnection
  };
}